import {
  axi,
  getDateFromIsoString,
  GetListOutput,
  GetListParams,
} from '@shared/api';
import {ChatChannel, ChatMessage} from '../types';

interface ChatAuthorOnBackend {
  id: string;
  username: string;
  avatar: string | null;
}

interface ChatMessageOnBackend {
  id: string;
  type: ChatMessage['type'];
  text: string | null;
  system_kind: string | null;
  created_at: string;
  deleted: boolean;
  author: ChatAuthorOnBackend | null;
}

interface GetTopicChatMessagesResponse {
  array: ChatMessageOnBackend[];
  page: number;
  per_page: number;
  total_pages: number;
  total: number;
}

interface ChatChannelOnBackend {
  channel_id: string;
}

const mapChatMessageFromBackend = ({
  id,
  type,
  text,
  system_kind,
  created_at,
  deleted,
  author,
}: ChatMessageOnBackend): ChatMessage => ({
  id,
  type,
  text,
  systemKind: system_kind,
  createdAt: getDateFromIsoString(created_at),
  deleted,
  author: author
    ? {
        id: author.id,
        username: author.username,
        avatarUrl: author.avatar,
      }
    : null,
});

export interface GetTopicChatMessagesParams extends GetListParams {
  topicSlug: string;
}

export const getTopicChatMessages = async ({
  topicSlug,
  page,
  pageSize,
}: GetTopicChatMessagesParams): Promise<GetListOutput<ChatMessage>> => {
  const response = await axi.get<GetTopicChatMessagesResponse>(
    `/admin/chat/topics/${topicSlug}/messages`,
    {params: {page, per_page: pageSize}},
  );
  const {
    array,
    page: responsePage,
    per_page,
    total,
    total_pages,
  } = response.data;

  return {
    items: array.map(message => mapChatMessageFromBackend(message)),
    page: responsePage,
    pageSize: per_page,
    total,
    totalPages: total_pages,
  };
};

export const getTopicChatChannel = async (
  topicSlug: string,
): Promise<ChatChannel> => {
  const response = await axi.get<ChatChannelOnBackend>(
    `/topics/${topicSlug}/chat`,
  );

  return {id: response.data.channel_id};
};

export const deleteChatMessage = async (
  channelId: string,
  messageId: string,
): Promise<void> => {
  await axi.delete(`/chat/channels/${channelId}/messages/${messageId}`);
};

export interface CreateChatBanParams {
  userId: string;
  channelId: string;
}

export const createChatBan = async ({
  userId,
  channelId,
}: CreateChatBanParams): Promise<void> => {
  await axi.post('/admin/chat/bans', {user_id: userId, channel_id: channelId});
};

export const createChatAnnouncement = async (
  channelId: string,
  text: string,
): Promise<void> => {
  await axi.post(`/admin/chat/channels/${channelId}/announcements`, {
    payload: {en: text},
  });
};
